import { logger } from "../../utils/logger";
import { formatMjcfNumber } from "./xmlGenerationHelpers";

type ExportBodyNode = {
  uuid?: unknown;
  name?: string;
  children?: ExportBodyNode[];
};

export const emitContactXml = ({
  bodyTree,
  contactExcludes,
  contactPairs,
  bodyUuidToXmlName,
  geomUuidToXmlName,
  sanitizeMjcfName,
  xmlEscapeAttr,
  arrayToString,
}: {
  bodyTree: ExportBodyNode[];
  contactExcludes: Array<{ body1: string; body2: string }> | undefined;
  contactPairs: unknown[] | undefined;
  bodyUuidToXmlName: Record<string, string>;
  geomUuidToXmlName: Record<string, string>;
  sanitizeMjcfName: (value: string) => string;
  xmlEscapeAttr: (value: string) => string;
  arrayToString: (values: Array<number | string>) => string;
}): string => {
  const buildAdjacencyPairs = (nodes: ExportBodyNode[]): Array<[string, string]> => {
    const pairs: Array<[string, string]> = [];
    const walk = (node: ExportBodyNode): void => {
      const parentName = node?.uuid
        ? bodyUuidToXmlName[String(node.uuid)] || sanitizeMjcfName(node.name || "")
        : sanitizeMjcfName(node.name || "");
      node.children?.forEach((child) => {
        const childName = child?.uuid
          ? bodyUuidToXmlName[String(child.uuid)] || sanitizeMjcfName(child.name || "")
          : sanitizeMjcfName(child.name || "");
        pairs.push([parentName, childName]);
        walk(child);
      });
    };
    nodes.forEach(walk);
    return pairs;
  };

  const adjPairs = buildAdjacencyPairs(bodyTree);
  const storedPairs: Array<[string, string]> = (contactExcludes || [])
    .map((ex) => {
      const b1 = bodyUuidToXmlName[String(ex.body1)] || sanitizeMjcfName(ex.body1);
      const b2 = bodyUuidToXmlName[String(ex.body2)] || sanitizeMjcfName(ex.body2);
      return b1 && b2 && b1 !== b2 ? ([b1, b2] as [string, string]) : null;
    })
    .filter((pair: [string, string] | null): pair is [string, string] => pair !== null);

  const adjSet = new Set(adjPairs.map(([a, b]) => `${a}\0${b}`));
  const uniqueStoredPairs = storedPairs
    .filter(([a, b]) => !adjSet.has(`${a}\0${b}`) && !adjSet.has(`${b}\0${a}`))
    .sort(([a1, b1], [a2, b2]) => `${a1}\0${b1}`.localeCompare(`${a2}\0${b2}`));
  const allPairs = [...adjPairs, ...uniqueStoredPairs];
  const contactPairLines: string[] = [];

  (contactPairs || [])
    .map((rawPair) => {
      const pair = rawPair as {
        uuid?: string;
        name?: string;
        geom1?: string;
        geom2?: string;
        condim?: unknown;
        priority?: unknown;
        friction?: unknown;
        solref?: unknown;
        solimp?: unknown;
        margin?: unknown;
        gap?: unknown;
      };
      const geom1 = geomUuidToXmlName[String(pair?.geom1 || "")] || "";
      const geom2 = geomUuidToXmlName[String(pair?.geom2 || "")] || "";
      const sortName = pair?.name ? sanitizeMjcfName(pair.name) : "";
      return { pair, geom1, geom2, sortKey: `${sortName}\0${geom1}\0${geom2}` };
    })
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .forEach(({ pair, geom1, geom2 }) => {
      if (!pair) return;
      if (!geom1 || !geom2 || geom1 === geom2) {
        logger.warn(
          `[xmlGenerationActions] Skipping contact pair "${pair.name || pair.uuid}" because one or both geoms could not be resolved.`
        );
        return;
      }

      let line = `    <pair`;
      if (pair.name) line += ` name="${xmlEscapeAttr(sanitizeMjcfName(pair.name))}"`;
      line += ` geom1="${xmlEscapeAttr(geom1)}" geom2="${xmlEscapeAttr(geom2)}"`;

      const condim = Number(pair.condim);
      if ([1, 3, 4, 6].includes(condim)) line += ` condim="${condim}"`;
      const priority = Number(pair.priority);
      if (Number.isFinite(priority) && Number.isInteger(priority)) {
        line += ` priority="${priority}"`;
      }
      if (Array.isArray(pair.friction) && pair.friction.length >= 5) {
        const friction = pair.friction.slice(0, 5).map(Number);
        if (friction.every(Number.isFinite)) {
          line += ` friction="${arrayToString(friction)}"`;
        }
      }
      if (Array.isArray(pair.solref) && pair.solref.length >= 2) {
        const solref = pair.solref.slice(0, 2).map(Number);
        if (solref.every(Number.isFinite)) {
          line += ` solref="${arrayToString(solref)}"`;
        }
      }
      if (Array.isArray(pair.solimp) && pair.solimp.length >= 5) {
        const solimp = pair.solimp.slice(0, 5).map(Number);
        if (solimp.every(Number.isFinite)) {
          line += ` solimp="${arrayToString(solimp)}"`;
        }
      }
      const margin = Number(pair.margin);
      if (Number.isFinite(margin)) line += ` margin="${formatMjcfNumber(margin)}"`;
      const gap = Number(pair.gap);
      if (Number.isFinite(gap)) line += ` gap="${formatMjcfNumber(gap)}"`;
      line += "/>\n";
      contactPairLines.push(line);
    });

  if (!allPairs.length && !contactPairLines.length) return "";

  let contactXml = "  <contact>\n";
  allPairs.forEach(([body1, body2]) => {
    contactXml += `    <exclude body1="${body1}" body2="${body2}"/>\n`;
  });
  contactPairLines.forEach((line) => {
    contactXml += line;
  });
  contactXml += "  </contact>\n\n";
  return contactXml;
};
